import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Modal, Badge } from '../ui';
import { useApiQuery } from '../../hooks/useApi';
import { combustibleService } from '../../services/combustibleService';
import { useFuelControlEnabled } from '../../hooks/useFuelControlEnabled';
import CapturaTurno from './CapturaTurno';
import type { TipoFotoTurno, EstadoConciliacion } from '../../types/combustible';

interface ControlCombustibleReporteSectionProps {
  reporteId: number;
  nivelCombustibleInicio?: number | null;
  nivelCombustibleFin?: number | null;
}

const badgeVariantPorEstado: Record<EstadoConciliacion, 'green' | 'yellow' | 'red' | 'gray'> = {
  OK: 'green',
  DISCREPANCIA_LITROS: 'yellow',
  FOTOS_PENDIENTES: 'red',
  SIN_CONSUMO_ESPECIFICO: 'gray',
  PENDIENTE: 'gray',
};

const ControlCombustibleReporteSection: React.FC<ControlCombustibleReporteSectionProps> = ({
  reporteId,
  nivelCombustibleInicio,
  nivelCombustibleFin,
}) => {
  const { t } = useTranslation();
  const { habilitado } = useFuelControlEnabled();
  const [capturaAbierta, setCapturaAbierta] = useState<TipoFotoTurno | null>(null);

  const { data: detalle, refetch } = useApiQuery(
    ['combustible-conciliacion-detalle', reporteId],
    () => combustibleService.getConciliacionDetalle(reporteId),
    { staleTime: 0, enabled: habilitado }
  );

  if (!habilitado) {
    return null;
  }

  const tiposConFoto = new Set((detalle?.fotos || []).map(f => f.tipo));

  return (
    <Card>
      <Card.Header>
        <h3 className="text-lg font-semibold">{t('fuel_control.report_section.title')}</h3>
      </Card.Header>
      <Card.Content>
        {detalle && (
          <div className="flex items-center gap-3 mb-4">
            <Badge variant={badgeVariantPorEstado[detalle.estado_conciliacion]}>
              {t(`fuel_control.status.${detalle.estado_conciliacion}`)}
            </Badge>
            {detalle.alerta && <span className="text-sm text-gray-600">{detalle.alerta}</span>}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-sm mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              {t('fuel_control.report_section.level_start')}
            </label>
            <div className="text-gray-900">{nivelCombustibleInicio != null ? `${nivelCombustibleInicio}%` : '-'}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              {t('fuel_control.report_section.level_end')}
            </label>
            <div className="text-gray-900">{nivelCombustibleFin != null ? `${nivelCombustibleFin}%` : '-'}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              {t('fuel_control.report_section.liters_calculated')}
            </label>
            <div className="text-gray-900">{detalle?.abastecimiento?.litros_calculados?.toFixed(1) ?? '-'}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              {t('fuel_control.report_section.liters_delivered')}
            </label>
            <div className="text-gray-900">{detalle?.abastecimiento?.litros_entregados?.toFixed(1) ?? '-'}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {!tiposConFoto.has('INICIO_TURNO') && (
            <Button variant="outline" size="sm" onClick={() => setCapturaAbierta('INICIO_TURNO')}>
              {t('fuel_control.report_section.upload_start')}
            </Button>
          )}
          {!tiposConFoto.has('FIN_TURNO') && (
            <Button variant="outline" size="sm" onClick={() => setCapturaAbierta('FIN_TURNO')}>
              {t('fuel_control.report_section.upload_end')}
            </Button>
          )}
        </div>
      </Card.Content>

      <Modal
        isOpen={!!capturaAbierta}
        onClose={() => setCapturaAbierta(null)}
        title={t('fuel_control.report_section.title')}
      >
        {capturaAbierta && (
          <CapturaTurno
            reporteId={reporteId}
            tipo={capturaAbierta}
            onSuccess={() => {
              setCapturaAbierta(null);
              refetch();
            }}
            onCancel={() => setCapturaAbierta(null)}
          />
        )}
      </Modal>
    </Card>
  );
};

export default ControlCombustibleReporteSection;
